import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, CheckCircle, XCircle, FileText, Trophy, AlertCircle, Users, User } from 'lucide-react';
import confetti from 'canvas-confetti';
import { authConfig } from '../config/auth';
import { userManager } from '../utils/auth';
import { useAuth } from '../contexts/AuthContext';
import type { Submission, Challenge } from '@crewcast/shared';
import { resolveAssets } from '@crewcast/shared';
import AssetLightbox, { type LightboxAsset } from '../components/AssetLightbox';
import LoadMoreButton from '../components/LoadMoreButton';
import PageLoader from '../components/PageLoader';
import '../styles/submissions.css';

interface LightboxState {
  assets: LightboxAsset[];
  index: number;
}

interface SubmissionStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

export default function SubmissionsPage() {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [submissionStats, setSubmissionStats] = useState<SubmissionStats | null>(null);
  const [challenges, setChallenges] = useState<Record<string, Challenge>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [newlyApprovedIds, setNewlyApprovedIds] = useState<Set<string>>(new Set());
  const celebrationFiredRef = useRef(false);
  const pendingCelebrationRef = useRef<string[]>([]);
  const cursorRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const { token, setUser } = useAuth();

  const openLightbox = (submission: Submission, startIndex = 0) => {
    const assets = resolveAssets(submission).map((a) => ({ url: a.url, type: a.type }));
    if (!assets.length) return;
    setLightbox({ assets, index: startIndex });
  };

  useEffect(() => {
    fetchSubmissions();
    userManager.refreshUser(authConfig.apiUrl).then(updatedUser => {
      if (updatedUser) setUser(updatedUser);
    });
  }, []);

  // Fire celebration after the page has finished loading and painted
  useEffect(() => {
    if (loading || pendingCelebrationRef.current.length === 0 || celebrationFiredRef.current) return;

    const unseenIds = pendingCelebrationRef.current;
    celebrationFiredRef.current = true;
    pendingCelebrationRef.current = [];

    requestAnimationFrame(() => {
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.5 },
        colors: ['#00bf62', '#ffffff', '#00a855', '#34d399'],
      });

      const msg =
        unseenIds.length === 1
          ? 'Awesome work! Your submission was approved!'
          : `Awesome work! ${unseenIds.length} submissions were approved!`;
      setToast(msg);
      setTimeout(() => setToast(null), 5000);

      fetch(`${authConfig.apiUrl}/api/submissions/mark-seen`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ submissionIds: unseenIds }),
      }).catch(() => {});
    });
  }, [loading]);

  const fetchSubmissions = async (fetchCursor: string | null = null, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError('');
    }

    try {
      if (!token) throw new Error('Not authenticated');

      const url = new URL(`${authConfig.apiUrl}/api/submissions`);
      url.searchParams.set('limit', '20');
      if (fetchCursor) url.searchParams.set('cursor', fetchCursor);

      const response = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to fetch submissions');

      const data = await response.json();
      const fetchedSubmissions: Submission[] = data.submissions;
      const nextCursor: string | null = data.nextCursor ?? null;

      if (append) {
        setSubmissions((prev) => [...prev, ...fetchedSubmissions]);
      } else {
        setSubmissions(fetchedSubmissions);
        if (data.stats) setSubmissionStats(data.stats);
        // Identify unseen approvals on initial load only
        if (!celebrationFiredRef.current) {
          const unseen = fetchedSubmissions.filter(
            (s) => s.approved === true && !s.approvalSeenAt
          );
          if (unseen.length > 0) {
            pendingCelebrationRef.current = unseen.map((s) => s.submissionId);
            setNewlyApprovedIds(new Set(unseen.map((s) => s.submissionId)));
          }
        }
      }

      const uniqueChallengeIds = Array.from(
        new Set(fetchedSubmissions.map((s) => s.challengeId))
      );
      await fetchChallenges(uniqueChallengeIds);

      cursorRef.current = nextCursor;
      setHasMore(nextCursor !== null);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const fetchChallenges = async (challengeIds: string[]) => {
    if (!token) return;

    const challengesMap: Record<string, Challenge> = {};

    await Promise.all(
      challengeIds.map(async (id) => {
        try {
          const response = await fetch(
            `${authConfig.apiUrl}/api/admin/challenges/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            challengesMap[id] = data.challenge;
          }
        } catch (err) {
          console.error(`Failed to fetch challenge ${id}:`, err);
        }
      })
    );

    setChallenges((prev) => ({ ...prev, ...challengesMap }));
  };

  const loadMore = () => {
    if (!hasMore || loadingMore || loading) return;
    fetchSubmissions(cursorRef.current, true);
  };

  const getStatusLabel = (approved: boolean | null | undefined) => {
    if (approved === null || approved === undefined) return { text: <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Clock size={16} /> Pending Review</span>, className: 'pending' };
    if (approved) return { text: <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><CheckCircle size={16} /> Approved</span>, className: 'approved' };
    return { text: <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><XCircle size={16} /> Rejected</span>, className: 'rejected' };
  };

  const filteredSubmissions = submissions.filter((sub) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return sub.approved === null;
    if (filter === 'approved') return sub.approved === true;
    if (filter === 'rejected') return sub.approved === false;
    return true;
  });

  const stats = submissionStats ?? {
    total: submissions.length,
    pending: submissions.filter((s) => s.approved === null).length,
    approved: submissions.filter((s) => s.approved === true).length,
    rejected: submissions.filter((s) => s.approved === false).length,
  };

  if (loading) {
    return (
      <div className="submissions-page-container">
        <PageLoader />
      </div>
    );
  }

  return (
      <div className="submissions-page-container">
        {toast && (
          <div className="approval-toast" role="status">
            <CheckCircle size={20} />
            {toast}
          </div>
        )}

        <div className="submissions-page-header">
          <h1>My Submissions</h1>
          <p className="page-subtitle">Track all your moment submissions and their status</p>
        </div>

      {/* Stats Cards */}
      <div className="submissions-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Submissions</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">Pending Review</div>
        </div>
        <div className="stat-card approved">
          <div className="stat-value">{stats.approved}</div>
          <div className="stat-label">Approved</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Rejected</div>
        </div>
      </div>

      {/* Filters */}
      <div className="submissions-filters">
        <button
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({stats.total})
        </button>
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending ({stats.pending})
        </button>
        <button
          className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
          onClick={() => setFilter('approved')}
        >
          Approved ({stats.approved})
        </button>
        <button
          className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Rejected ({stats.rejected})
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Submissions List */}
      {filteredSubmissions.length === 0 ? (
        <div className="no-submissions">
          <div className="no-submissions-icon">
            <FileText size={48} color="#00bf62" />
          </div>
          <h2>No submissions yet</h2>
          <p>
            {filter === 'all'
              ? 'Start participating in moments to earn points!'
              : `You have no ${filter} submissions.`}
          </p>
          <button onClick={() => navigate('/moments')} className="cta-btn">
            Browse Moments
          </button>
        </div>
      ) : (
        <div className="submissions-grid">
          {filteredSubmissions.map((submission) => {
            const challenge = challenges[submission.challengeId];
            const status = getStatusLabel(submission.approved);

            return (
              <div key={submission.submissionId} className="submission-card-large">
                {/* Submission Preview */}
                <div className="submission-preview">
                  {(() => {
                    const assets = resolveAssets(submission);
                    const primary = assets[0];
                    return (
                      <>
                        {primary.type === 'video' ? (
                          <video src={`${primary.url}#t=0.001`} className="submission-media" muted playsInline preload="metadata" />
                        ) : (
                          <img src={primary.url} alt="Submission" className="submission-media" />
                        )}
                        {assets.length > 1 && (
                          <span className="submission-asset-count-badge">
                            +{assets.length - 1} more
                          </span>
                        )}
                      </>
                    );
                  })()}
                  <button
                    className="media-tap-target"
                    onClick={() => openLightbox(submission)}
                    aria-label="View submission"
                  />
                  <span className={`submission-status-badge ${status.className}${newlyApprovedIds.has(submission.submissionId) ? ' new-approval' : ''}`}>
                    {status.text}
                  </span>
                </div>

                {/* Submission Info */}
                <div className="submission-card-content">
                  {/* Challenge Info */}
                  {challenge && (
                    <div className="challenge-info-compact">
                      <h3
                        className="challenge-title-link"
                        onClick={() => navigate(`/moments/${challenge.challengeId}`)}
                      >
                        {challenge.title}
                      </h3>
                      {(challenge.points ?? 0) > 0 && (
                        <span className="challenge-points" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Trophy size={16} /> {challenge.points} points
                        </span>
                      )}
                    </div>
                  )}

                  {/* Caption */}
                  {submission.caption && (
                    <p className="submission-caption">{submission.caption}</p>
                  )}

                  {/* Rejection reason (only when rejected) */}
                  {submission.approved === false && submission.rejectionReason && (
                    <div className="submission-rejection-reason">
                      <strong>Reason:</strong> {submission.rejectionReason}
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="submission-metadata">
                    <div className="metadata-item">
                      <span className="metadata-label">Submitted:</span>
                      <span className="metadata-value">
                        {new Date(submission.submittedAt).toLocaleDateString()} at{' '}
                        {new Date(submission.submittedAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>

                    {submission.reviewedAt && (
                      <div className="metadata-item">
                        <span className="metadata-label">Reviewed:</span>
                        <span className="metadata-value">
                          {new Date(submission.reviewedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {submission.collaborators && submission.collaborators.length > 0 && (
                      <>
                        {submission.submitterName && (
                          <div className="metadata-item">
                            <span className="metadata-label">Submitted by:</span>
                            <span className="metadata-value">
                              <User size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                              {submission.submitterName}
                            </span>
                          </div>
                        )}
                        <div className="metadata-item">
                          <span className="metadata-label">
                            Collaborator{submission.collaborators.length > 1 ? 's' : ''}:
                          </span>
                          <span className="metadata-value">
                            <Users size={14} style={{ verticalAlign: 'text-bottom', marginRight: 4 }} />
                            {submission.collaboratorNames && submission.collaboratorNames.length > 0
                              ? submission.collaboratorNames.join(', ')
                              : `${submission.collaborators.length} collaborator${submission.collaborators.length > 1 ? 's' : ''}`}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="submission-actions">
                    {challenge && (
                      <button
                        onClick={() => navigate(`/moments/${challenge.challengeId}`)}
                        className="view-challenge-btn"
                      >
                        View Moment
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {lightbox && (
        <AssetLightbox
          assets={lightbox.assets}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
          onIndexChange={(i) => setLightbox((lb) => lb ? { ...lb, index: i } : lb)}
        />
      )}
    </div>
  );
}

